import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import cn from 'classnames';
import { Badge, Divider, Row, Tooltip, Typography, useDevice } from 'seagull';
import { Edit } from 'seagull/icons';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import { useLocaleKeys } from '~/i18n';
import { serviceModel } from '~/models';
import { billingBadgesTextsPath, discountBadge } from '~/utils/service-description/getServiceDescriptionBadges';
import IncludedServices from '../IncludedServices/IncludedServices';
import BadgesList from './BadgesList/BadgesList';
import BulletPointsSection from './BulletPointsSection/BulletPointsSection';
import EditablePrice from './EditablePrice/EditablePrice';
import PriceDisplay from './PriceDisplay/PriceDisplay';
import { ServiceBillingInlineSettings } from './ServiceBillingInlineSettings/ServiceBillingInlineSettings';
import ServiceDescription from './ServiceDescription/ServiceDescription';
import styles from './ServiceDetails.module.scss';
import { getPausedBadge, getPriceDisplayProps, getStatusBadgeText } from './ServiceDetails.utils';
export default function ServiceDetails(props) {
    var _service_bundle;
    const { testId, menu, agreementData, showBadges, showBulletPoints, billingInitiationProps, isFreshlyAdded = false, additionalSections, isVendor, disableEditBillUpfront, disableEditBillingDay, disableStatusBadge = false, disableEditCost, disableEditBillingInitiation, isReadOnly, ignoreAmendments, error, serviceOrigin, relationshipState, onCostChange, onHasError, onDayOfMonthChange, onDayOfWeekChange, onBillUpfrontModeChange, onChangeService, onDayOfYearChange, onIncludedServicesChange, onPausedChange, onBillOnAcceptanceChange, onStartBillingAtChange, context, onEditServiceNameAndDescription, isDragActive, dragHandle } = props;
    const { t: serviceDetailsTexts } = useLocaleKeys('components.serviceDetails');
    const { t: billingBadgesTexts } = useLocaleKeys(billingBadgesTextsPath);
    const { isMobileDevice } = useDevice();
    const service = ignoreAmendments ? props.service : serviceModel.applyChanges(props.service);
    const isActiveAgreement = agreementData.isActiveAgreement();
    const statusBadgeText = getStatusBadgeText(service, isActiveAgreement, disableStatusBadge);
    const pausedBadge = getPausedBadge(service, isVendor, isActiveAgreement);
    const discountBadgeData = discountBadge(service, billingBadgesTexts);
    const priceDisplayProps = getPriceDisplayProps(service, agreementData);
    const isBundle = serviceModel.isBundle(service);
    const enableEditServiceNameAndDescription = !!onEditServiceNameAndDescription && !isReadOnly;
    const showReadMoreButton = !enableEditServiceNameAndDescription;
    const isEditMode = !!relationshipState;
    const showPausedBadge = !(isEditMode && !isReadOnly);
    function renderCoreContent() {
        const isEditable = !isDragActive && enableEditServiceNameAndDescription;
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.descriptionAndMenuButtonContainer,
                    children: [
                        dragHandle && /*#__PURE__*/ _jsx("div", {
                            className: styles.dragHandleSlot,
                            children: dragHandle
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: cn(styles.detailedDescription, {
                                [styles.withBadge]: !!statusBadgeText
                            }),
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: cn(styles.serviceNameAndDescriptionContainer, {
                                    [styles.clickable]: isEditable
                                }),
                                onClick: isEditable ? ()=>onEditServiceNameAndDescription(service) : undefined,
                                children: [
                                    /*#__PURE__*/ _jsxs(Row, {
                                        alignItems: "center",
                                        className: styles.serviceNameRow,
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "h5",
                                                ellipsis: true,
                                                testId: testId + 'name',
                                                children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                                    text: service.name,
                                                    mode: "text"
                                                })
                                            }),
                                            isEditable && /*#__PURE__*/ _jsx(Row, {
                                                className: styles.editIconButton,
                                                testId: testId + 'inline-edit',
                                                children: /*#__PURE__*/ _jsx(Edit, {})
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.serviceDescriptionContainer,
                                        children: /*#__PURE__*/ _jsx(ServiceDescription, {
                                            service: service,
                                            testId: testId,
                                            showReadMoreButton: showReadMoreButton
                                        })
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.badgeAndDotMenuContainer,
                            children: [
                                pausedBadge && showPausedBadge && /*#__PURE__*/ _jsx(Tooltip, {
                                    title: pausedBadge.tooltip,
                                    placement: "top",
                                    children: /*#__PURE__*/ _jsx(Badge, {
                                        outlined: true,
                                        testId: "service-details-paused-badge",
                                        children: billingBadgesTexts.paused.text()
                                    })
                                }),
                                statusBadgeText && /*#__PURE__*/ _jsx(Badge, {
                                    outlined: true,
                                    testId: "".concat(testId, "status-badge"),
                                    children: statusBadgeText
                                }),
                                !isDragActive && menu
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.finalPriceContainer,
                    children: [
                        !isDragActive && onCostChange && onChangeService ? /*#__PURE__*/ _jsx(EditablePrice, {
                            agreementData: agreementData,
                            serviceOrigin: serviceOrigin,
                            serviceWithChanges: service,
                            service: props.service,
                            testId: testId,
                            disableEditCost: disableEditCost,
                            isReadOnly: isReadOnly,
                            showDiscountError: !!priceDisplayProps.error,
                            onCostChange: onCostChange,
                            onChangeService: onChangeService
                        }) : /*#__PURE__*/ _jsx(PriceDisplay, _object_spread_props(_object_spread({}, priceDisplayProps), {
                            testId: testId
                        })),
                        showBadges ? /*#__PURE__*/ _jsx(BadgesList, {
                            service: service,
                            agreementData: agreementData,
                            testId: testId,
                            isFreshlyAdded: isFreshlyAdded,
                            context: context,
                            isReadOnly: isReadOnly
                        }) : discountBadgeData && /*#__PURE__*/ _jsx("div", {
                            "data-testid": testId + 'discount',
                            children: /*#__PURE__*/ _jsx(Badge, {
                                skin: discountBadgeData.skin,
                                children: discountBadgeData.text
                            })
                        })
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.serviceDetails,
        "data-testid": "".concat(testId, "service-details"),
        "data-serviceid": service.id,
        children: [
            renderCoreContent(),
            !isDragActive && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    isBundle && /*#__PURE__*/ _jsxs("div", {
                        className: styles.includedServicesContainer,
                        children: [
                            isReadOnly && /*#__PURE__*/ _jsx(Divider, {
                                margin: 12
                            }),
                            /*#__PURE__*/ _jsx(IncludedServices, {
                                showErrors: error,
                                mode: isReadOnly ? 'readonly' : 'editAndDelete',
                                value: ((_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services) || [],
                                onChange: (value)=>onIncludedServicesChange && onIncludedServicesChange(value),
                                tooltip: serviceDetailsTexts.hiddenRateTooltip(),
                                disableMobileCard: true
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ServiceBillingInlineSettings, {
                        service: service,
                        testId: testId,
                        agreementData: agreementData,
                        billingInitiation: billingInitiationProps,
                        relationshipState: relationshipState,
                        isFreshlyAdded: isFreshlyAdded,
                        showBillingSettingsSection: !showBulletPoints,
                        disableBillingInitiation: disableEditBillingInitiation,
                        disableBillUpfront: disableEditBillUpfront,
                        disableBillingDay: disableEditBillingDay,
                        showError: error,
                        billingSettingsClassName: styles.section,
                        isReadOnly: isReadOnly,
                        onChangeService: onChangeService,
                        onDayOfMonthChange: onDayOfMonthChange,
                        onDayOfWeekChange: onDayOfWeekChange,
                        onDayOfYearChange: onDayOfYearChange,
                        onBillUpfrontModeChange: onBillUpfrontModeChange,
                        onHasError: onHasError,
                        onPausedChange: onPausedChange,
                        onBillOnAcceptanceChange: onBillOnAcceptanceChange,
                        onStartBillingAtChange: onStartBillingAtChange
                    }),
                    showBulletPoints && /*#__PURE__*/ _jsx(BulletPointsSection, _object_spread({}, props)),
                    additionalSections && additionalSections.length > 0 && /*#__PURE__*/ _jsx(Divider, {
                        margin: isMobileDevice ? 12 : 24
                    }),
                    additionalSections === null || additionalSections === void 0 ? void 0 : additionalSections.map((param, idx)=>{
                        let { value, disableBorder } = param;
                        return value && /*#__PURE__*/ _jsx("div", {
                            className: cn(styles.additionalSection, disableBorder && styles.disableBorder, isMobileDevice && styles.mobile),
                            children: value
                        }, idx);
                    })
                ]
            })
        ]
    });
}
